import { EnvironmentProviders, provideAppInitializer } from '@angular/core';
import { getApp, initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { initializeAppCheck, provideAppCheck, ReCaptchaEnterpriseProvider } from '@angular/fire/app-check';
import { Capacitor } from '@capacitor/core';
import { prepareAppCheck } from '../../app-check.bootstrap';
import { environment } from '../../environments/environment';

function assertEnterpriseSiteKey(siteKey: string): void {
  if (!siteKey || siteKey.includes('REPLACE')) {
    console.warn(
      '[App Check] Missing STAGING_APP_CHECK_RECAPTCHA_SITE_KEY. Run npm run env:sync after editing secrets.env.',
    );
  }
}

function provideFirebaseCore(): EnvironmentProviders[] {
  return [
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideFirestore(() => getFirestore()),
    provideAuth(() => getAuth()),
    provideStorage(() => getStorage()),
    provideFunctions(() => getFunctions(getApp(), environment.functionsRegion)),
  ];
}

/** Browser bootstrap — includes App Check and reCAPTCHA preparation. */
export function provideAppFirebase(): EnvironmentProviders[] {
  const siteKey = environment.appCheckRecaptchaSiteKey.trim();
  assertEnterpriseSiteKey(siteKey);

  return [
    provideAppInitializer(() =>
      prepareAppCheck().catch(err => {
        console.error('[App Check] Error al preparar reCAPTCHA Enterprise:', err);
        throw err;
      }),
    ),
    ...provideFirebaseCore(),
    provideAppCheck(() => {
      // Native Capacitor WebView can't run reCAPTCHA Enterprise (capacitor://localhost is not
      // an authorized domain). Use a fixed debug token registered in Firebase Console instead.
      const debugToken = environment.appCheckDebugToken;
      if (Capacitor.isNativePlatform() && debugToken) {
        (self as unknown as Record<string, unknown>)['FIREBASE_APPCHECK_DEBUG_TOKEN'] = debugToken;
      } else if (environment.firebaseAppCheckDebug) {
        console.info(
          '[App Check] Modo depuración. Copia el token de la consola → Firebase → App Check → Manage debug tokens.',
        );
      }

      return initializeAppCheck(getApp(), {
        provider: new ReCaptchaEnterpriseProvider(siteKey),
        isTokenAutoRefreshEnabled: true,
      });
    }),
  ];
}

/** SSR/prerender — Firestore reads only; no App Check (no browser APIs). */
export function provideAppFirebaseServer(): EnvironmentProviders[] {
  return provideFirebaseCore();
}
